import { MapUtils } from '../../../utility/map-utils.js';
import { cloneMap, isSameData, isSameMap, mapToData } from '../../dto/data-model-utils.js';
import { DataModel, DataModelBuilder, SerializationContext, SerializedData } from '../../dto/data-model.js';
import { BusinessModuleRef } from '../business-module-ref.js';
import { OpenAccessPass } from './open-access-pass.js';

export class OpenAccessPassPool extends DataModel {
  static readonly TYPE_ID: string = '67eb164872284979a6b47108c696337e';

  private constructor(
    public readonly businessRef: BusinessModuleRef,
    private readonly soldPasses: Map<string, number>,
  ) {
    super();
  }

  get $typeId(): string {
    return OpenAccessPassPool.TYPE_ID;
  }

  static create(args: {
    businessRef: BusinessModuleRef,
    soldPasses: Map<string, number>,
  }): OpenAccessPassPool {
    return new OpenAccessPassPool(
        args.businessRef,
        args.soldPasses,
    );
  }

  static empty(businessRef: BusinessModuleRef): OpenAccessPassPool {
    return this.create({
      businessRef: businessRef,
      soldPasses: new Map<string, number>(),
    });
  }

  static fromMap(context: SerializationContext, map: SerializedData): OpenAccessPassPool {
    return new OpenAccessPassPool(
        BusinessModuleRef.mustDecode(MapUtils.get<string>(map, 'businessRef')),
        MapUtils.getMap(map, 'soldPasses', (k, v) => ({
          key: k,
          value: Number(v),
        })),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'businessRef': this.businessRef.encode(),
      'soldPasses': mapToData(this.soldPasses, (k, v) => ({
        key: k,
        value: v,
      })),
    };
  }

  clone(): this {
    return new OpenAccessPassPool(
        this.businessRef.clone(),
        cloneMap(this.soldPasses, (v) => v),
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return isSameMap(other.soldPasses, this.soldPasses, (lh, rh) => lh === rh) &&
          isSameData(other.businessRef, this.businessRef);
  }

  getSoldPassCount(templateId: string): number {
    return this.soldPasses.get(templateId) ?? 0;
  }

  onPassSold(pass: OpenAccessPass): void {
    const templateId = pass.templateId;
    const currentSoldAmount = this.getSoldPassCount(templateId);

    this.soldPasses.set(templateId, currentSoldAmount + 1);
  }

  onPassCancelled(pass: OpenAccessPass): void {
    const templateId = pass.templateId;
    const currentSoldAmount = this.getSoldPassCount(templateId);

    this.soldPasses.set(templateId, currentSoldAmount - 1);
  }
}

export class OpenAccessPassPoolBuilder extends DataModelBuilder<OpenAccessPassPool> {
  build(context: SerializationContext, map: SerializedData): OpenAccessPassPool {
    return OpenAccessPassPool.fromMap(context, map);
  }
}
